import * as THREE from 'three';
import { eventBus } from './EventBus.js';

export class LabelManager {
    constructor(sceneManager, deviceManager) {
        this.sceneManager = sceneManager;
        this.deviceManager = deviceManager;
        this.camera = sceneManager.camera;
        this.renderer = sceneManager.renderer;
        
        // Device detection
        this.isMobile = this.detectMobile();
        console.log(`LabelManager initialized: ${this.isMobile ? 'Mobile' : 'Desktop'} mode (threshold: ${this.isMobile ? 12 : 5})`);
        
        // Configuration with device-specific defaults
        this.config = {
            proximityThreshold: this.isMobile ? 12 : 5,  // Mobile: 12, Desktop: 5
            proximityFadeRange: this.isMobile ? 6 : 3,    // Mobile: 6, Desktop: 3
            persistentScale: 0.85,
            defaultScale: 1.0,
            animationDuration: 150
        };
        
        // State
        this.labels = new Map();
        this.hoveredDevice = null;
        this.isPersistentMode = false;
        this.isProximityEnabled = true;
        
        // Raycaster for hover detection
        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();
        
        // DOM container
        this.container = null;
        
        this.init();
    }
    
    // Device detection method
    detectMobile() {
        const hasTouch = window.matchMedia('(pointer: coarse)').matches;
        const smallScreen = window.innerWidth <= 768;
        const mobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        
        return hasTouch || smallScreen || mobileUA;
    }
    
    // Re-detect device on resize (optional)
    recalculateDeviceType() {
        const wasMobile = this.isMobile;
        this.isMobile = this.detectMobile();
        
        if (wasMobile !== this.isMobile) {
            console.log(`Device type changed: ${this.isMobile ? 'Mobile' : 'Desktop'}`);
            // Update defaults
            this.config.proximityThreshold = this.isMobile ? 12 : 5;
            this.config.proximityFadeRange = this.isMobile ? 6 : 3;
        }
    }
    
    init() {
        this.createContainer();
        this.setupEventListeners();
        this.createLabelsForDevices();
    }
    
    createContainer() {
        this.container = document.createElement('div');
        this.container.id = 'label-overlay';
        this.container.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 50;
        `;
        document.body.appendChild(this.container);
    }
    
    createLabelsForDevices() {
        eventBus.on('devices:loaded', (devices) => {
            devices.forEach(device => {
                this.createLabelElement(device);
            });
        });
        
        eventBus.on('device:created', (device) => {
            this.createLabelElement(device);
        });
    }
    
    createLabelElement(device) {
        const label = document.createElement('div');
        label.className = 'device-label';
        label.dataset.deviceId = device.id;
        label.innerHTML = `
            <div class="label-id">${device.id}</div>
            <div class="label-location">${device.location}</div>
        `;
        
        label.style.cssText = `
            position: absolute;
            transform: translate(-50%, -100%);
            opacity: 0;
            pointer-events: none;
            transition: opacity ${this.config.animationDuration}ms ease,
                        transform ${this.config.animationDuration}ms ease;
        `;
        
        this.container.appendChild(label);
        this.labels.set(device.id, {
            element: label,
            device: device,
            lastDistance: Infinity
        });
    }
    
    setupEventListeners() {
        window.addEventListener('mousemove', (e) => {
            this.mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
            this.mouse.y = -(e.clientY / window.innerHeight) * 2 + 1;
        });
        
        // Recalculate on resize (optional - removes if you don't want auto-switching)
        window.addEventListener('resize', () => {
            this.recalculateDeviceType();
        });
        
        // Toggle persistent mode
        eventBus.on('labels:toggle', (isActive) => {
            this.isPersistentMode = isActive;
        });
        
        // Config update event
        eventBus.on('labels:config', (newConfig) => {
            Object.assign(this.config, newConfig);
            console.log('LabelManager config updated:', this.config);
        });
    }
    
    // PUBLIC METHOD: Set threshold for current device
    setProximityThreshold(value) {
        this.config.proximityThreshold = value;
        console.log(`Proximity threshold set to ${value} (${this.isMobile ? 'mobile' : 'desktop'})`);
    }
    
    // PUBLIC METHOD: Set fade range for current device
    setFadeRange(value) {
        this.config.proximityFadeRange = value;
        console.log(`Fade range set to ${value}`);
    }
    
    // PUBLIC METHOD: Force device type and apply defaults
    forceDeviceType(type) {
        if (type === 'mobile') {
            this.isMobile = true;
            this.config.proximityThreshold = 40;
            this.config.proximityFadeRange = 8;
        } else if (type === 'desktop') {
            this.isMobile = false;
            this.config.proximityThreshold = 20;
            this.config.proximityFadeRange = 5;
        }
        console.log(`Forced ${type} mode: threshold=${this.config.proximityThreshold}`);
    }
    
    // PUBLIC METHOD: Get current device info
    getDeviceInfo() {
        return {
            isMobile: this.isMobile,
            threshold: this.config.proximityThreshold,
            fadeRange: this.config.proximityFadeRange,
            screenWidth: window.innerWidth
        };
    }
    
    getDistanceToCamera(position) {
        return this.camera.position.distanceTo(position);
    }
    
    worldToScreen(position) {
        const vector = position.clone();
        vector.project(this.camera);
        
        const x = (vector.x * 0.5 + 0.5) * window.innerWidth;
        const y = (-(vector.y * 0.5) + 0.5) * window.innerHeight;
        
        return { x, y, visible: vector.z < 1 };
    }
    
    checkHover() {
        this.raycaster.setFromCamera(this.mouse, this.camera);
        
        const deviceMeshes = [];
        this.deviceManager.devices.forEach((device) => {
            deviceMeshes.push(device.mesh);
        });
        
        const intersects = this.raycaster.intersectObjects(deviceMeshes, true);
        
        let hoveredId = null;
        if (intersects.length > 0) {
            const hitObject = intersects[0].object;
            this.deviceManager.devices.forEach((device, id) => {
                if (this.isDescendant(hitObject, device.mesh)) {
                    hoveredId = id;
                }
            });
        }
        
        if (hoveredId !== this.hoveredDevice) {
            this.hoveredDevice = hoveredId;
            eventBus.emit('device:hover', hoveredId);
        }
    }
    
    isDescendant(child, parent) {
        let current = child;
        while (current) {
            if (current === parent) return true;
            current = current.parent;
        }
        return false;
    }
    
    shouldShowLabel(id, distance, screenPos) {
        // Priority 1: Persistent mode shows all (if in view)
        if (this.isPersistentMode && screenPos.visible) {
            return { show: true, mode: 'persistent', opacity: 0.85 };
        }
        
        // Priority 2: Hover always shows
        if (this.hoveredDevice === id) {
            return { show: true, mode: 'hover', opacity: 1.0 };
        }
        
        // Priority 3: Proximity (if enabled)
        if (this.isProximityEnabled && screenPos.visible) {
            const threshold = this.config.proximityThreshold;
            const fadeRange = this.config.proximityFadeRange;
            
            if (distance < threshold) {
                return { show: true, mode: 'proximity', opacity: 0.9 };
            } else if (distance < threshold + fadeRange) {
                const fadeProgress = (distance - threshold) / fadeRange;
                const opacity = 0.9 * (1 - fadeProgress);
                return { show: opacity > 0.1, mode: 'proximity', opacity };
            }
        }
        
        return { show: false, mode: 'hidden', opacity: 0 };
    }
    
    updateLabel(id, labelData) {
        const { element, device } = labelData;
        const position = device.mesh.position.clone();
        position.y += 0.5;
        
        const screenPos = this.worldToScreen(position);
        const distance = this.getDistanceToCamera(device.mesh.position);
        
        const visibility = this.shouldShowLabel(id, distance, screenPos);
        
        if (visibility.show) {
            element.style.left = `${screenPos.x}px`;
            element.style.top = `${screenPos.y}px`;
            
            const scale = visibility.mode === 'persistent' 
                ? this.config.persistentScale 
                : this.config.defaultScale;
            
            element.style.opacity = visibility.opacity;
            element.style.transform = `translate(-50%, -100%) scale(${scale})`;
            element.className = `device-label ${visibility.mode}`;
        } else {
            element.style.opacity = '0';
            element.className = 'device-label';
        }
        
        labelData.lastDistance = distance;
    }
    
    update() {
        this.checkHover();
        this.labels.forEach((labelData, id) => {
            this.updateLabel(id, labelData);
        });
    }
    
    destroy() {
        if (this.container) {
            this.container.remove();
        }
        this.labels.clear();
    }
}